/**
 * BlurText — text that animates from blurred to focused, word by word
 * or character by character. Triggered when the element scrolls into view.
 *
 * Inspired by React Bits. Uses Framer Motion (no extra deps).
 */

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'

interface BlurTextProps {
  text: string
  /** Split and animate by 'words' or 'characters'. Default: 'words' */
  animateBy?: 'words' | 'characters'
  /** Delay between each unit in ms. Default: 80 */
  delay?: number
  /** Animation duration per unit in seconds. Default: 0.4 */
  stepDuration?: number
  /** Direction the text slides from. Default: 'bottom' */
  direction?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  /** Custom starting state */
  animationFrom?: Record<string, string | number>
  /** Custom ending state */
  animationTo?: Record<string, string | number>
  onAnimationComplete?: () => void
}

export function BlurText({
  text,
  animateBy = 'words',
  delay = 80,
  stepDuration = 0.4,
  direction = 'bottom',
  className,
  animationFrom,
  animationTo,
  onAnimationComplete,
}: BlurTextProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })

  const units = animateBy === 'words' ? text.split(' ') : text.split('')

  const directionOffset = {
    top: { y: -16 },
    bottom: { y: 16 },
    left: { x: -16 },
    right: { x: 16 },
  }

  const from = animationFrom ?? {
    filter: 'blur(8px)',
    opacity: 0,
    ...directionOffset[direction],
  }
  const to = animationTo ?? {
    filter: 'blur(0px)',
    opacity: 1,
    x: 0,
    y: 0,
  }

  return (
    <span ref={ref} className={className}>
      {units.map((unit, i) => (
        <motion.span
          key={`${unit}-${i}`}
          initial={from}
          animate={inView ? to : from}
          transition={{
            duration: stepDuration,
            delay: (i * delay) / 1000,
            ease: [0.22, 1, 0.36, 1],
          }}
          onAnimationComplete={i === units.length - 1 ? onAnimationComplete : undefined}
          style={{ display: 'inline-block', willChange: 'filter, opacity, transform' }}
        >
          {unit}
          {animateBy === 'words' && i < units.length - 1 ? '\u00A0' : ''}
        </motion.span>
      ))}
    </span>
  )
}
